/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { gql, NetworkStatus, type TypedDocumentNode } from '@apollo/client'
import { type QueryRef, useMutation, useReadQuery } from '@apollo/client/react'
import { ErrorAlert } from '@repo/shared-client/components/blocks'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import SFPageLoading from '@web/src/components/primatives/sf-page-loading/sf-page-loading'
import type { PaymentRequestFormInput } from '@web/src/features/admin/payment-request-form/payment-request-form'
import PaymentRequestForm from '@web/src/features/admin/payment-request-form/payment-request-form'
import { env } from '@web/src/lib/env.client'
import { useTranslation } from '@web/src/lib/intl/react'
import { UPDATE_PAYMENT_APPROVED } from '@web/src/lib/mutations'
import { robotsMetaForEnvironment, seo } from '@web/src/lib/seo'
import { useState } from 'react'
import toast from 'react-hot-toast'
import type { EditPaymentApprovedQuery, EditPaymentApprovedQueryVariables } from 'types/graphql'

const EDIT_PAYMENT_APPROVED_QUERY: TypedDocumentNode<EditPaymentApprovedQuery, EditPaymentApprovedQueryVariables> = gql`
  query EditPaymentApprovedQuery($paymentApprovedId: String!) {
    paymentApproved(id: $paymentApprovedId) {
      id
      profileId
      days
      partnerIds
      total
      notes
      refunded
    }
  }
`

export const Route = createFileRoute('/(auth)/(admin-layout)/admin/payments/approved/$paymentApprovedId/edit')({
  component: EditPaymentApprovedPage,
  head: () =>
    seo({
      title: `Edit accepted payment | ${env.VITE_APP_NAME}`,
      description: 'Edit accepted payment record',
      robots: robotsMetaForEnvironment({ all: 'noindex,nofollow' }),
    }),
  loader: ({ context, params }) => {
    const queryRef = context.preloadQuery(EDIT_PAYMENT_APPROVED_QUERY, {
      variables: { paymentApprovedId: params.paymentApprovedId },
    })
    return { queryRef }
  },
})

function EditPaymentApprovedPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { paymentApprovedId } = Route.useParams()
  const { queryRef } = Route.useLoaderData()
  const { data, error, networkStatus } = useReadQuery(queryRef as QueryRef<EditPaymentApprovedQuery>)
  const [showSpinner, setShowSpinner] = useState(false)

  const [updatePaymentApproved] = useMutation(UPDATE_PAYMENT_APPROVED, {
    onCompleted: (result) => {
      toast.success(t('Payment record updated'))
      setShowSpinner(false)
      navigate({
        to: '/admin/payments',
        search: { tab: result.updatePaymentApproved.refunded ? 'refunded' : 'approved' },
      } as never)
    },
    onError: (err) => {
      toast.error(err.message)
      setShowSpinner(false)
    },
  })

  const onSave = (input: PaymentRequestFormInput) => {
    setShowSpinner(true)
    updatePaymentApproved({
      variables: {
        id: paymentApprovedId,
        input: {
          days: input.days,
          partnerIds: input.partnerIds,
          total: input.total,
          notes: input.notes,
        },
      },
    })
  }

  const isInitialLoading = networkStatus === NetworkStatus.loading
  if (isInitialLoading) return <SFPageLoading />
  if (error) return <ErrorAlert error={error} />

  const row = data?.paymentApproved
  if (!row) return <ErrorAlert error={new Error(t('Payment record not found'))} />

  return (
    <div className="mx-auto w-full max-w-2xl pt-4">
      <div className="flex w-full flex-col gap-4">
        <h1 className="w-full text-center font-semibold text-3xl tracking-tight">{t('Edit accepted payment')}</h1>
        <PaymentRequestForm
          initialValues={{
            profileId: row.profileId,
            days: row.days,
            partnerIds: row.partnerIds ?? [],
            total: row.total,
            notes: row.notes,
          }}
          mode="edit"
          onSave={onSave}
          showAdminNotes
          showSpinner={showSpinner}
        />
      </div>
    </div>
  )
}
